/*
  What we can say about HubSpot, in one place.

  The Clean Pro figures and the trademark notice are read by /agency and by
  /hubspot-aeo. They used to live only in `agency.ts`, and a second copy would
  mean two places to update the same marketplace numbers. That is the bug this
  file's neighbour documents at length: $500 was once claimed as the Portfolio
  price while /pricing listed that tier at $299, because one number had two
  homes.

  The figures are dated in the notice because they are somebody else's, read
  off a listing that changes. If they are refreshed, the date moves with them.
*/

/*
  The Clean Pro figures, as numbers rather than as sentences.

  They are somebody else's, read off a listing that changes, so each one
  carries where it came from: the Sourced Number Rule wants the source in the
  block and in the reader's line of sight, not in a footnote.

  `figures` below is built from these so /agency and /hubspot-aeo cannot say
  two different things about the same three numbers.
*/
export const cleanProStats = [
  {
    num: '5.0',
    said: 'stars',
    more: 'The rating on the Clean Pro listing.',
    from: 'ecosystem.hubspot.com, August 2026',
  },
  {
    num: '214',
    said: 'reviews, 100% five-star',
    more: 'Every review the listing carries, not a selection of them.',
    from: 'ecosystem.hubspot.com, August 2026',
  },
  {
    num: '3,000+',
    said: 'installs listed',
    more: 'The best-selling theme in the marketplace\u2019s history.',
    from: 'ecosystem.hubspot.com, August 2026',
  },
]

export const cleanPro = {
  headline: 'From the team behind Clean Pro, the best-selling theme in HubSpot marketplace history.',
  figures: cleanProStats.map((s) => `${s.num} ${s.said}`),
}

/*
  Required wherever a surface names HubSpot, including the robot views and the
  generated markdown, which is why it lives in a content module and not in a
  page. A missing trademark notice is a legal problem, not a cosmetic one.
*/
export const hubspotLegal =
  'SuperSchema works with HubSpot. SuperSchema is not affiliated with, sponsored by, or endorsed by HubSpot, Inc. HubSpot and the HubSpot logo are registered trademarks of HubSpot, Inc. Clean Pro marketplace figures as listed on ecosystem.hubspot.com, August 2026.'

/*
  The tier requirement, stated plainly, and the plan it is included from.

  Pushing schema into HubSpot pages and blog posts needs Marketing Hub
  Professional or Enterprise, or CMS Hub Professional or Enterprise. A Starter
  site cannot use the deploy, and the software hands it the markup instead.

  This is the one place the rule against naming hubs gives way, because here
  the tier is the fact. Every surface that promises the deploy states this or
  links to the page that does: a visitor who reads a page, starts a trial, and
  then finds their plan cannot do the thing we sold them is the worst outcome
  this work can produce, and it costs the trial at the moment of highest trust.

  The same sentence, word for word, on /pricing, /products/deploy and the
  home page (Kevin, 2026-09-15). `deployFrom` names our plan the deploy is
  included from. Nothing in the server gates the HubSpot deploy on a plan
  tier, so it is included from the first paid plan; a test pins the name to
  the shared plan config so a renamed tier cannot leave this sentence stale.
*/
export const deployFrom = 'Essential'

export const paidTier = {
  short: 'on a paid HubSpot plan',
  full:
    `Your HubSpot plan must be Marketing Hub Professional or Enterprise, or CMS Hub Professional or Enterprise. On a Starter plan SuperSchema generates everything and hands you the markup to paste. Deploying to HubSpot CMS is included from ${deployFrom}.`,
}
